import { Box, Flex, Text } from "@chakra-ui/react";
import { useMemo } from "react";
import { useMutation } from "react-query";
import { useEarmold, useEarmoldVariants } from "../hooks/earmolds.hooks";
import { fetchFileContent } from "../mutations/file.mutations";
import { type NewOrderForm, useNewOrderStore } from "../stores/order.store";
import type { VariantOption } from "../types/earmold.types";
import FileIconStl from "./icons/files/FileIconStl";
import { getLongExposedId } from "../services/command.service";
import { getHasDisplayableVariantOption } from "../utils/earFormDisplay";
import { getHasVariantMismatchWarning } from "./getHasVariantMismatchWarning";
import { getIsMaterialVariantLabel } from "./getIsMaterialVariantLabel";
import { useMaterialByLabel } from "../hooks/material.hooks";
import { useFile } from "../hooks/file.hooks";

const KeyValue = ({
	earmold,
	id,
	option,
	oppositeOption,
	hasLeftEar,
	hasRightEar,
}: {
	earmold?: number | string;
	id: string;
	option?: VariantOption | null;
	oppositeOption?: VariantOption | null;
	hasLeftEar: boolean;
	hasRightEar: boolean;
}) => {
	const { data: variants } = useEarmoldVariants(earmold);
	const label = useMemo(() => {
		const variantFound = variants?.find(
			(variant) => variant.id.toString() === id.toString(),
		);
		if (variantFound) {
			return variantFound.label;
		}
		return "--";
	}, [variants, id]);
	const hasVariantMismatchWarning = getHasVariantMismatchWarning({
		hasLeftEar,
		hasRightEar,
		currentValue: option,
		oppositeValue: oppositeOption,
	});
	const isMaterialVariant = getIsMaterialVariantLabel(label);
	const material = useMaterialByLabel(
		isMaterialVariant ? (option?.label ?? undefined) : undefined,
	);
	const materialFileState = useFile({ id: material?.fileId });
	return (
		<Flex
			justifyContent="space-between"
			borderBottom="solid 1px"
			borderColor="gray.200"
			mx={2}
			px={2}
			py={2}
			alignItems={"center"}
		>
			<Text
				lineHeight="18px"
				fontSize="12px"
				fontWeight="400"
				color="gray.500"
				w="100%"
			>
				{label}
			</Text>
			<Flex
				alignItems="center"
				gap={2}
				w="100%"
				color={hasVariantMismatchWarning ? "orange.500" : undefined}
			>
				{isMaterialVariant && materialFileState.data?.url && (
					<img
						className="size-5 aspect-square"
						src={materialFileState.data.url}
						alt={`matiere ${option?.uuid ?? ""}`}
					/>
				)}
				<Text
					lineHeight="21px"
					fontSize="14px"
					fontWeight="400"
					textAlign="left"
				>
					{option?.label}
				</Text>
			</Flex>
		</Flex>
	);
};

const RightEarCard = ({
	earmold,
	form,
}: {
	earmold?: number | string;
	form?: NewOrderForm;
}) => {
	const { rightFile, command, hasRightEar, hasLeftEar, leftEarForm } =
		useNewOrderStore();
	const rightNewFileName = command
		? `${getLongExposedId(command)}_R.stl`
		: (rightFile?.fileName ?? "file");
	const { mutate: downloadFileContent } = useMutation(fetchFileContent, {
		onSuccess: (content: string) => {
			const blob = new Blob([content]);
			const link = document.createElement("a");
			link.href = URL.createObjectURL(blob);
			link.download = rightNewFileName;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		},
	});
	const { data: instance } = useEarmold(earmold);

	if (!hasRightEar) {
		return <Box></Box>;
	}
	return (
		<Box w="100%" border="solid 1px" borderColor="gray.200" borderRadius="xl">
			<Flex
				bgColor="#FEF2F2"
				borderTopRadius="xl"
				justifyContent="center"
				py={6}
				borderBottom="solid 1px"
				borderColor="gray.200"
				direction="column"
				alignItems="center"
			>
				<Text lineHeight="21px" fontSize="16px" fontWeight="600">
					Détails de l'oreille droite
				</Text>
				<Text lineHeight="18px" fontSize="13px" fontWeight="500" mt={2}>
					{instance?.label ?? "---"}
				</Text>
				{rightFile && command && (
					<Flex
						justify="center"
						justifyContent="center"
						alignItems="center"
						gap={4}
						my={3}
						onClick={() => {
							if (rightFile) {
								downloadFileContent(rightFile.id);
							}
						}}
					>
						<Flex gap={4} alignItems="center">
							<FileIconStl w="24px" h="24px" />
							<Flex direction={"column"}>
								<Text fontSize="14px" fontWeight={400}>
									{rightNewFileName}
								</Text>
								<Text fontSize="12px" fontWeight={400}>
									{rightFile.fileName}
								</Text>
							</Flex>
						</Flex>
					</Flex>
				)}
			</Flex>
			<Flex direction="column">
				{form &&
					Object.entries(form)
						.filter(([, value]) => getHasDisplayableVariantOption(value))
						.map(([label, value]) => (
							<KeyValue
								key={label}
								earmold={earmold}
								id={label}
								option={value}
								oppositeOption={leftEarForm[label]}
								hasLeftEar={hasLeftEar ?? false}
								hasRightEar={hasRightEar ?? false}
							/>
						))}
			</Flex>
		</Box>
	);
};

export default RightEarCard;
